/**
 * The push master switch (TRUS-697): `notification_preferences.mobile_push_enabled`
 * (GET / PUT /api/notifications/preferences). Despite the stored name it covers
 * EVERY device - the WeaveIn app on phones and tablets and every browser with
 * notifications turned on: the server checks it before it looks up any device
 * (services/message_push.py), so "off" means no push anywhere. Only an explicit
 * `false` turns it off; a missing value is on.
 *
 * One shared, lazily loaded value for the page (the settings toggle, the
 * browser-notifications card and the "Turn on notifications" banner all read
 * it). Staff only: the member portal has no such switch. A failed load leaves
 * it unknown (`undefined`), which every reader treats as "not known to be off".
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/services/apiClient';

const PATH = '/api/notifications/preferences';

let current: boolean | undefined;
let loading: Promise<void> | null = null;
const listeners = new Set<(v: boolean | undefined) => void>();

function publish(v: boolean | undefined) {
  current = v;
  listeners.forEach((fn) => fn(v));
}

export function loadPushMasterSwitch(force = false): Promise<void> {
  if (loading && !force) return loading;
  loading = apiClient
    .get(PATH)
    .then((res) => publish(res?.data?.mobile_push_enabled !== false))
    .catch(() => {
      /* unknown: readers keep their default behaviour */
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

export async function savePushMasterSwitch(on: boolean): Promise<void> {
  const before = current;
  publish(on);
  try {
    await apiClient.put(PATH, { mobile_push_enabled: on });
  } catch (err) {
    publish(before);
    throw err;
  }
}

/** Test hook: forget the cached value. */
export function resetPushMasterSwitch(value?: boolean): void {
  current = value;
  loading = null;
  listeners.forEach((fn) => fn(value));
}

export function usePushMasterSwitch(active = true): {
  enabled: boolean | undefined;
  setEnabled: (on: boolean) => Promise<void>;
} {
  const [enabled, setLocal] = useState<boolean | undefined>(current);
  useEffect(() => {
    if (!active) return undefined;
    listeners.add(setLocal);
    setLocal(current);
    if (current === undefined) void loadPushMasterSwitch();
    return () => {
      listeners.delete(setLocal);
    };
  }, [active]);
  const setEnabled = useCallback((on: boolean) => savePushMasterSwitch(on), []);
  return { enabled: active ? enabled : undefined, setEnabled };
}
